import React from "react";
import { datadogRum } from "@datadog/browser-rum";
import getConfig from "next/config";
import { isEmpty, get } from "lodash";
import Image from "next/image";
import { useTranslation } from "react-i18next";
import { useRouter } from "next/router";
import { getIsOnpremise } from "../utils";
import getEnv from "../utils/getEnv";

interface DatadogProviderProps {
  user?: any;
  authData?: any;
  children: JSX.Element;
}

const isOnpremise = getIsOnpremise();

class DatadogProvider extends React.Component<
  DatadogProviderProps,
  { [hasError: string]: any }
> {
  constructor(props: DatadogProviderProps) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }

  initialiseDatadog = () => {
    const { publicRuntimeConfig } = getConfig();
    const commitHash = getEnv("VERCEL_GIT_COMMIT_SHA")?.substring(0, 7);
    datadogRum.init({
      applicationId: getEnv("NEXT_PUBLIC_DATADOG_APP_ID") || "",
      clientToken: getEnv("NEXT_PUBLIC_DATADOG_CLIENT_TOKEN") || "",
      site: "datadoghq.eu",
      service: getEnv("NEXT_PUBLIC_DATADOG_SERVICE"),
      version: commitHash
        ? `${publicRuntimeConfig?.version}.${commitHash}`
        : publicRuntimeConfig?.version, //Fallback if VERCEL_GIT_COMMIT_SHA is not set
      env: window && window.location && window.location.hostname,
      sessionSampleRate: 100,
      sessionReplaySampleRate: 0,
      trackLongTasks: true,
      trackResources: true, //MONITORS ALL XHR/Fetch requests in the browswer -> inside axios library
      trackUserInteractions: true,
      //trackViewsManually: true,
      defaultPrivacyLevel: "mask-user-input",
    });
    // datadogRum.startSessionReplayRecording();
  };

  componentDidMount() {
    if (getEnv("NEXT_PUBLIC_APP_ENV") === "production") {
      this.initialiseDatadog();
    }
  }

  componentDidUpdate(prevProps: DatadogProviderProps) {
    const { user: prevUser = {} } = prevProps;
    const { user = {}, authData = {} } = this.props;
    const organizations = get(authData, "authData", {});
    const sources = get(authData, "sources", []);
    const readSources = get(authData, "read_sources", []);
    const uploadSources = get(authData, "upload_sources", []);

    if (prevUser.email !== user.email && getEnv("NEXT_PUBLIC_APP_ENV") === "production") {
      datadogRum.setUser({
        name: user.name && user.name !== "undefined" ? user.name : user.email,
        email: user.email,
        language: navigator?.language,
        readSources: readSources.map((readSource: any) => {
          const name = get(readSource, ["name"], "");
          return name;
        }),
        uploadSources: uploadSources.map((uploadSource: any) => {
          const name = get(uploadSource, ["name"], "");
          return name;
        }),
        sources: sources?.map((source: string | number) => {
          if (isEmpty(organizations[source])) {
            return source;
          }
          return organizations[source];
        }),
      });
    }
  }

  componentDidCatch(error: unknown) {
    if (getEnv("NEXT_PUBLIC_APP_ENV") === "production") {
      datadogRum.addError(error, {
        scope: "error-boundary",
      });
    }
  }
  render() {
    if (this.state.hasError) {
      return <Fallback />;
    }
    return this.props.children;
  }
}

const Fallback = () => {
  const { t } = useTranslation();
  const router = useRouter();

  const handleReturn = () => {
    router.push("/patients").then(() => {
      router.reload();
    });
  };

  return (
    <>
      {isOnpremise ? (
        <div className="flex items-center justify-center bold text-gray-900 dark:text-white bg-gray-100 dark:bg-gray-900 w-full h-full">
          {t("loading_key")}...
        </div>
      ) : (
        <div className="flex h-full bg-[#0C1723] flex-col justify-center items-center space-y-6">
          <div className="relative w-[312px] h-[232px] md:w-[588px] md:h-[436px]">
            <Image
              fill
              alt="error"
              src="/assets/images/error-image.svg"
              className="object-contain"
            />
          </div>
          <button
            onClick={handleReturn}
            className="bg-gray-800 text-teal-300 text-lg md:text-2xl py-5 md:py-6 px-20 rounded-xl"
          >
            {t("return_to_worklist")}
          </button>
        </div>
      )}
    </>
  );
};

export default DatadogProvider;
